import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import useNow from '../../../hooks/useNow';
import useWorkspaceUploads from '../../../hooks/useWorkspaceUploads';
import { ACCEPTED_UPLOAD_EXTENSIONS, isAcceptedUpload, MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, MAX_UPLOAD_FILES, MAX_UPLOAD_FILES_MESSAGE, MAX_UPLOAD_FILES_CHIP, isTiedAllocation, effectiveProfileId } from '../../../utils/uploadNames';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  runTransaction,
  deleteField,
  query,
  where,
  orderBy,
  onSnapshot,
  documentId,
} from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { approveWorkspaceFiles } from '../../../services/workspaceApprovals';
import { registerUploadBatch } from '../../../services/uploadRegistration';
import UploadRegistrationErrors from './UploadRegistrationErrors';
import { formatText } from '../../common/TextFormatterProvider';
import { formatDisplayValue } from '../../../utils/tagValueUtils';
import { toMillis } from '../../../utils/timestamps';
import AllocationCard from './AllocationCard';
import PackAttributePicker from './PackAttributePicker';
import FileCard from './FileCard';
import FileReviewDialog from './FileReviewDialog';
import TableBucket from './TableBucket';
import DuplicateFilesPrompt, { splitLikelyDuplicates } from '../../common/DuplicateFilesPrompt';
import TriageFileCard, { deriveTriageAction, initialTriageSelection, triageActionTableIds } from './TriageFileCard';
import { isExternalTable } from '../../../utils/tableTypes';
import { isActiveProfile, buildProfileNameById, profileName, byProfileName } from '../../../utils/profileDisplay';
import { allocationAttributes, missingMandatoryAttributes, tagsToValueMap } from '../../../utils/mandatoryAttributes';
import { invalidPackAttributes, tableCarriesPackAttributes } from '../../../utils/packAttributes';
import { batchBlockedReason, buildReadyPlan, noReadyReason, readyHoverLines } from '../../../utils/workspaceApproval';
import { settleEach, describeFailures } from '../../../utils/perMember';
import { findExistingFiles, findExistingFilesAcrossTables } from '../../../services/existingFiles';
import ExistingFilesPrompt from '../../common/ExistingFilesPrompt';
import TransitionCard from './TransitionCard';
import { useAttributeCategories } from '../../../contexts/AttributesContext';
import { sortNewestFirst } from '../../../utils/filePages';
import useFileSectionPages from '../../../hooks/useFileSectionPages';

const IN_QUERY_BATCH = 30;     // Firestore 'in' filter cap
// Batch numbers are derived, never persisted, so they shift when the query window
// slides. Every batch therefore also shows when it was dropped.
const relativeTime = (millis) => {
  if (!millis) return '';
  const seconds = Math.max(0, Math.round((Date.now() - millis) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

// The table ids a workspace upload's routing entry points at ({table_ids} from the
// relay, legacy {table_id} on older bucket-drop registrations).
const routedTableIds = (entry) => entry?.table_ids || (entry?.table_id ? [entry.table_id] : []);

// A user's pending profile choice, held per table staging document.
const selectionKey = (tableId, fileId) => `${tableId}:${fileId}`;

// Everything a bucket holds, transition placeholders included.
const cardSetCount = (cards) => (
  cards.triage.length + cards.routing.length + cards.allocations.length
  + cards.preparing.length + cards.files.length);

const emptyCardSet = () => ({ triage: [], routing: [], allocations: [], preparing: [], files: [] });

// The triage cards a batch approval would write: every member the allocator has
// settled ('pending') whose live chip state yields an action. Analysing,
// Failed and unassigned members are never written — they are counted instead.
const buildBatchPlan = (members) => {
  const approvable = [];
  let skipped = 0;
  members.forEach((file) => {
    const action = file.status === 'pending' ? deriveTriageAction(file, file.selection) : null;
    if (action) approvable.push({ file, action });
    else skipped += 1;
  });
  return { approvable, skipped };
};

// One destination table's listener set: data profiles, table staging documents and
// the batches' file documents. No unit document is subscribed: a file card is drawn
// from its file document alone. Headless — exactly one instance runs per destination
// table however many batches route into it, and its results are published upward so
// the parent can place them in that table's bucket.
export function TableDataFeed({ companyId, tableId, tableName, memberIds, onData, onError }) {
  const [dataProfiles, setDataProfiles] = useState([]);
  const [stagingFiles, setStagingFiles] = useState([]);
  const [filesById, setFilesById] = useState({});
  // Boundary bookkeeping replaces the arrays even when membership is unchanged.
  // Only the sorted IDs determine the lifetime of these queries.
  const memberIdsKey = JSON.stringify([...new Set(memberIds)].sort());

  useEffect(() => {
    const profilesRef = collection(db, `users/${companyId}/tables/${tableId}/data_profiles`);
    const unsubscribe = onSnapshot(query(profilesRef), (snapshot) => {
      setDataProfiles(snapshot.docs.map(d => ({ ...d.data(), id: d.id })).sort(byProfileName));
    }, (err) => {
      console.error('Error listening to data profiles:', err);
      onError(`Failed to load data profiles for ${formatText(tableName || tableId)}`);
    });
    return () => unsubscribe();
  }, [companyId, tableId, tableName, onError]);

  useEffect(() => {
    const stagingQuery = query(
      collection(db, `users/${companyId}/tables/${tableId}/unallocated_files`),
      orderBy('created_at', 'desc')
    );
    const unsubscribe = onSnapshot(stagingQuery, (snapshot) => {
      setStagingFiles(snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          fileName: data.name || data.fileName || 'Unknown File',
        };
      }));
    }, (err) => {
      console.error('Error watching unallocated files:', err);
      onError('Failed to watch unallocated file updates');
    });
    return () => unsubscribe();
  }, [companyId, tableId, onError]);

  // The relayed members keep their workspace ids, which approve_allocation reuses as
  // the file's id — a multi-target member appears under the same id in each
  // destination.
  useEffect(() => {
    const memberIds = JSON.parse(memberIdsKey);
    if (memberIds.length === 0) return undefined;
    const unsubscribers = [];
    for (let i = 0; i < memberIds.length; i += IN_QUERY_BATCH) {
      const batchIds = memberIds.slice(i, i + IN_QUERY_BATCH);
      const filesQuery = query(
        collection(db, `users/${companyId}/tables/${tableId}/files`),
        where(documentId(), 'in', batchIds)
      );
      unsubscribers.push(onSnapshot(filesQuery, (snapshot) => {
        setFilesById(prev => {
          const next = { ...prev };
          batchIds.forEach(id => { delete next[id]; });
          snapshot.docs.forEach(d => { next[d.id] = { id: d.id, ...d.data() }; });
          return next;
        });
      }, (err) => {
        console.error('Error watching files:', err);
        onError('Failed to watch file updates');
      }));
    }
    return () => unsubscribers.forEach(unsubscribe => unsubscribe());
  }, [companyId, tableId, memberIdsKey, onError]);

  const memberIdSet = useMemo(() => new Set(JSON.parse(memberIdsKey)), [memberIdsKey]);
  const tableFiles = useMemo(() => Object.values(filesById)
    .filter(f => memberIdSet.has(f.id))
    .sort((a, b) => toMillis(b.created_at) - toMillis(a.created_at)), [filesById, memberIdSet]);

  useEffect(() => {
    onData(tableId, { dataProfiles, stagingFiles, tableFiles });
  }, [tableId, dataProfiles, stagingFiles, tableFiles, onData]);

  return null;
}

// What the user is told when some of a selection did not register (H28).
const notRegisteredMessage = (failed) => (
  `${failed.length} file(s) did not upload — add them again: `
  + `${failed.map(({ file }) => file.name).join(', ')}.`
);

// One batch's control row: its attributes, its progress, its approve button, and any
// files that still have no destination. Files that DO have one have moved onto the
// board below, so a fully-assigned batch collapses to the header line — its approve
// button has to stay reachable from somewhere.
function BatchRow({ row, isApproving, onApproveBatch, children }) {
  return (
    <Box sx={{ pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        mb: row.undecided.length > 0 ? 1.5 : 0,
      }}>
        <Typography variant="subtitle2">Batch {row.batchNo}</Typography>
        <Typography variant="caption" color="text.secondary">{relativeTime(row.createdAt)}</Typography>
        {row.attributePairs.map(([category, value]) => (
          <Chip
            key={category}
            label={`${formatText(category)}: ${formatDisplayValue(value)}`}
            size="small"
            variant="outlined"
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {row.registering
            ? `${row.registeredCount} of ${row.memberCount} file${row.memberCount === 1 ? '' : 's'} uploaded`
            : row.undecided.length > 0
            ? `${row.undecided.length} of ${row.memberCount} need a destination`
            : `all ${row.memberCount} file${row.memberCount === 1 ? '' : 's'} assigned`}
        </Typography>
        <Tooltip title={batchBlockedReason(row)}>
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={() => onApproveBatch(row)}
              disabled={Boolean(batchBlockedReason(row)) || isApproving}
            >
              Approve batch
            </Button>
          </span>
        </Tooltip>
      </Box>
      {children}
    </Box>
  );
}

function WorkspaceFilesHome({ companyId, tables, hiddenTables }) {
  // Relative run times ("just now", "Yest") and the routing cards' ages follow
  // the shared clock.
  const now = useNow();
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [packAttributes, setPackAttributes] = useState([]);
  // The same pending save freezes the picker and both upload entry points.
  const [isSavingPackAttribute, setIsSavingPackAttribute] = useState(false);
  const { categories: attributeCategories } = useAttributeCategories();
  const [workspaceFiles, setWorkspaceFiles] = useState([]);
  const { uploads: recentUploads, visibleUploads, loading: uploadsLoading,
    checking: uploadsChecking, hasOlder: mayHaveOlderUploads, error: uploadsError,
    loadOlder, retry: retryUploads } = useWorkspaceUploads(companyId, workspaceFiles);
  // The last pick on the batch zone, or on a bucket, held too many files.
  const [tooManyFiles, setTooManyFiles] = useState(false);
  const [overCapBucketId, setOverCapBucketId] = useState(null);
  const [tableDataById, setTableDataById] = useState({});
  const [profileSelections, setProfileSelections] = useState({});
  const [optimisticTablesByUpload, setOptimisticTablesByUpload] = useState({});
  const [approveTarget, setApproveTarget] = useState(null);
  // Newly picked files that look like ones already selected, awaiting Keep / Discard.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  // A selection some of whose files already exist where they are going, held
  // for Upload Anyway / Skip Duplicates: { conflicts, chosen, scope, proceed } (H28).
  const [existingPrompt, setExistingPrompt] = useState(null);
  const uploadOperationRef = useRef(null);
  const existingPromptRef = useRef(null);
  useEffect(() => {
    setIsUploading(false);
    setExistingPrompt(null);
    setUploadProgress({ current: 0, total: 0 });
    return () => {
      uploadOperationRef.current?.controller.abort();
      uploadOperationRef.current = null;
      existingPromptRef.current = null;
    };
  }, [companyId]);
  const [isApprovingTriage, setIsApprovingTriage] = useState(false);
  const [isApprovingBoard, setIsApprovingBoard] = useState(false);
  const [hiddenExpanded, setHiddenExpanded] = useState(false);
  // The file whose review dialog is open, { tableId, fileId }. The dialog is held at
  // the page rather than in a card: an edit reruns the file, which can move it to
  // another bucket page and unmount the card that opened the dialog.
  const [reviewTarget, setReviewTarget] = useState(null);
  // Caption only, read once on mount: the backend evaluates the setting itself
  // at decision time, so this is a hint, not authority.
  const [workspaceAutoTriage, setWorkspaceAutoTriage] = useState(false);
  const fileInputRef = useRef(null);
  const triageSelections = useRef(new Map());

  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'users', companyId))
      .then(snap => setWorkspaceAutoTriage(snap.exists() && snap.data().workspace_auto_triage === true))
      .catch(() => {});   // caption only — a failed read just leaves it hidden
  }, [companyId]);

  // Stable identity: a fresh `{}` fallback each render would rebuild the board memo.
  const hiddenMap = useMemo(() => hiddenTables || {}, [hiddenTables]);

  // Only tables with an ingestion pipeline can be a destination.
  const routableTables = useMemo(() => (tables || []).filter(t => !isExternalTable(t)), [tables]);
  const tablesById = useMemo(() => {
    const m = new Map();
    (tables || []).forEach((t) => { m.set(t.id, t); });
    return m;
  }, [tables]);

  useEffect(() => {
    if (!companyId) return undefined;
    setWorkspaceFiles([]);
    const stagingQuery = query(
      collection(db, `users/${companyId}/unallocated_files`),
      orderBy('created_at', 'desc')
    );
    const unsubscribe = onSnapshot(stagingQuery, (snapshot) => {
      setWorkspaceFiles(snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          fileName: data.name || data.fileName || 'Unknown File',
          selection: triageSelections.current.get(d.id) ?? initialTriageSelection(data),
        };
      }));
    }, (error) => {
      console.error('Error watching workspace files:', error);
      setUploadError('Failed to watch uploaded file updates');
    });
    return () => unsubscribe();
  }, [companyId]);

  // Batch numbers ascend with the drop order across the loaded window. The query is
  // descending, so this re-sorts: the oldest visible batch is 1.
  const batchNumberById = useMemo(() => {
    const ascending = [...visibleUploads].sort(
      (a, b) => toMillis(a.created_at) - toMillis(b.created_at));
    return new Map(ascending.map((upload, index) => [upload.id, index + 1]));
  }, [visibleUploads]);

  const batchLabelFor = useCallback((uploadId) => {
    const number = batchNumberById.get(uploadId);
    return number ? `Batch ${number}` : null;
  }, [batchNumberById]);

  // Destination registry: the members each recent upload routed to each table, in
  // the order the tables first appear in routing.
  const memberIdsByTable = useMemo(() => {
    const map = new Map();
    visibleUploads.forEach(upload => {
      Object.entries(upload.routing || {}).forEach(([memberId, entry]) => {
        routedTableIds(entry).forEach(tableId => {
          if (!map.has(tableId)) map.set(tableId, []);
          const members = map.get(tableId);
          if (!members.includes(memberId)) members.push(memberId);
        });
      });
    });
    Object.values(optimisticTablesByUpload).forEach(tableIds => {
      tableIds.forEach(tableId => {
        if (!map.has(tableId)) map.set(tableId, []);
      });
    });
    return map;
  }, [visibleUploads, optimisticTablesByUpload]);

  // A bucket is always on screen, but a bucket with no members needs no listener, so
  // feeds stay demand-driven. The optimistic set is what starts a table's feed in the
  // window between approving a card and the relay writing `routing`; a bucket drop
  // needs no entry because it writes its own routing before it resolves.
  const tableFeeds = useMemo(() => Array.from(memberIdsByTable.entries())
    .filter(([tableId]) => tablesById.has(tableId))
    .map(([tableId, memberIds]) => ({ tableId, memberIds })),
  [memberIdsByTable, tablesById]);

  const handleTableData = useCallback((tableId, data) => {
    setTableDataById(prev => ({ ...prev, [tableId]: data }));
  }, []);

  // Selection surfaces and approve gates restrict to Active profiles; name
  // resolution spans the full list so deleted profiles still render a name.
  const tableViewsById = useMemo(() => {
    const views = {};
    Object.entries(tableDataById).forEach(([tableId, data]) => {
      const dataProfiles = data.dataProfiles || [];
      const profileById = {};
      dataProfiles.forEach((p) => { profileById[p.id] = p; });
      views[tableId] = {
        ...data,
        activeProfiles: dataProfiles.filter(isActiveProfile),
        nameById: buildProfileNameById(dataProfiles),
        profileById,
      };
    });
    return views;
  }, [tableDataById]);

  // Attributes persist across uploads, so a bucket drop is judged against the picker's
  // CURRENT state, while an already-registered triage card keeps being judged against
  // the attributes it was uploaded with.
  const currentAttributeMap = useMemo(() => tagsToValueMap(packAttributes), [packAttributes]);
  const isCarrierNow = useCallback(
    (table) => tableCarriesPackAttributes(table, currentAttributeMap),
    [currentAttributeMap]);
  const carrierTablesFor = useCallback(
    (file) => routableTables.filter(t => tableCarriesPackAttributes(t, file.pack_attributes || {})),
    [routableTables]);

  const blockedReasonFor = useCallback((table) => {
    const names = new Set((table?.attributeColumns || []).map(col => col.name));
    const missing = Object.keys(currentAttributeMap).filter(category => !names.has(category));
    return `This table has no ${missing.map(formatText).join(', ')} attribute column, so it `
      + 'cannot store the attributes on this drop. Remove the attribute, or add the column '
      + 'to this table.';
  }, [currentAttributeMap]);

  // Approved cards are mid-relay: they have left the user's court and come back as
  // an AllocationCard in their destination bucket (or bounce to 'pending' with an
  // error note).
  const triageFiles = useMemo(
    () => workspaceFiles.filter(f => f.status !== 'approved'), [workspaceFiles]);

  // Mid-relay members: the approval wrote `allocations` and `status: 'approved'`, and
  // the document survives until the relay transaction deletes it — long enough to name
  // the buckets the member is on its way to and hold the space there.
  const relayingFiles = useMemo(
    () => workspaceFiles.filter(f => f.status === 'approved' && f.upload_id && f.allocations),
    [workspaceFiles]);

  // Legacy staging docs registered before the workspace boundary existed: no batch
  // to belong to, so they keep their own cards and their individual approval.
  const ungroupedTriageFiles = useMemo(
    () => triageFiles.filter(f => !f.upload_id && !deriveTriageAction(f, f.selection)),
    [triageFiles]);

  // One row per batch that still has a pre-decision member. A batch retires from the
  // strip once every one of its files has been approved; its cards live on below.
  const batchRows = useMemo(() => {
    const stagingIds = new Set(workspaceFiles.map(file => file.id));
    const membersByUpload = new Map();
    triageFiles.forEach(file => {
      if (!file.upload_id) return;
      if (!membersByUpload.has(file.upload_id)) membersByUpload.set(file.upload_id, []);
      membersByUpload.get(file.upload_id).push(file);
    });

    const rows = [];
    membersByUpload.forEach((members, uploadId) => {
      const upload = visibleUploads.find(u => u.id === uploadId);
      const undecided = members.filter(file => !(
        file.status === 'pending' && deriveTriageAction(file, file.selection)));
      // Attributes outlive triage: a member's own copy while one is still in the queue,
      // the boundary's provenance copy once every member has been relayed.
      const attributeMap = members[0]?.pack_attributes || upload?.pack_attributes || {};
      // The manifest names every member before its file has transferred, and the
      // backend refuses approval until the boundary seals ('uploaded'). A manifest
      // id still 'pending' with no staging document (any status) has not
      // registered; the seal alone is not proof, as the boundary and staging
      // listeners can land out of order.
      const manifest = upload?.raw_manifest || {};
      const manifestIds = Object.keys(manifest);
      const unregistered = manifestIds.filter(id => manifest[id] === 'pending' && !stagingIds.has(id));
      rows.push({
        uploadId,
        batchNo: batchNumberById.get(uploadId),
        createdAt: toMillis(upload?.created_at) || toMillis(members[0]?.created_at),
        attributePairs: Object.entries(attributeMap),
        memberCount: manifestIds.length || members.length,
        registeredCount: manifestIds.length - unregistered.length,
        registering: !upload || upload.status === 'registering' || unregistered.length > 0,
        failed: Boolean(upload && !['registering', 'uploaded'].includes(upload.status)),
        undecided,
        hasAnalysing: members.some(f => f.status === 'analysing'),
        plan: buildBatchPlan(members),
      });
    });
    return rows.sort((a, b) => b.createdAt - a.createdAt);
  }, [triageFiles, workspaceFiles, visibleUploads, batchNumberById]);

  // The persistent board: one bucket per routable table, seeded rather than derived, so
  // an empty table is still a visible drop target. Every batch feeds the same buckets.
  const board = useMemo(() => {
    const multi = {
      key: 'multi', tableId: null, title: 'Multi', hidden: false,
      cards: emptyCardSet(), allocationEntries: [],
    };
    const byTable = new Map();
    routableTables.forEach(table => {
      byTable.set(table.id, {
        key: `table:${table.id}`,
        tableId: table.id,
        title: formatText(table.name || table.id),
        hidden: !!hiddenMap[table.id],
        // Judged against the picker's CURRENT selection, so clearing an attribute
        // re-enables and reorders every destination it had ruled out.
        isCarrier: tableCarriesPackAttributes(table, currentAttributeMap),
        cards: emptyCardSet(),
        allocationEntries: [],
        stagedIds: new Set(),
        fileIds: new Set(),
        approvedStaging: [],
        relaying: [],
      });
    });

    // Post-relay lifecycle, from the tables that have a feed mounted. Staging documents
    // are scoped to members this page's batches routed there, so a file uploaded from a
    // table's own page does not appear here.
    Object.entries(tableViewsById).forEach(([tableId, view]) => {
      const bucket = byTable.get(tableId);
      if (!bucket) return;
      const members = new Set(memberIdsByTable.get(tableId) || []);
      (view.stagingFiles || []).forEach(file => {
        if (!members.has(file.id)) return;
        bucket.stagedIds.add(file.id);
        // Approved: approve_allocation is creating the file and will delete this
        // document — hold the space until the file's card can take over.
        if (file.status === 'approved') {
          bucket.approvedStaging.push(file);
          return;
        }
        // Local choice, then the choice the document holds (kept when approval
        // bounces the file back), then the proposal — none for a tie.
        bucket.cards.allocations.push({
          ...file,
          selected_data_profile: profileSelections[selectionKey(tableId, file.id)]
            ?? file.selected_data_profile
            ?? (isTiedAllocation(file) ? '' : file.proposed_data_profile),
        });
      });
      // Filtered on the member set as well as by the feed: a feed that unmounts (its
      // last batch settled, say) leaves its last snapshot behind in tableDataById, and
      // without this the cards it published would outlive the batch that owned them.
      (view.tableFiles || []).forEach(file => {
        if (!members.has(file.id)) return;
        bucket.fileIds.add(file.id);
        bucket.cards.files.push(file);
      });
    });

    // Pre-decision cards sit under the destination their chips currently name, so
    // editing the chips moves the card. One with no destination stays in its batch
    // row; one naming several lands in Multi.
    triageFiles.forEach(file => {
      const action = file.status === 'pending' ? deriveTriageAction(file, file.selection) : null;
      if (!action) return;
      const bucket = action.table_ids.length === 1
        ? byTable.get(action.table_ids[0])
        : multi;
      (bucket || multi).cards.triage.push(file);
    });

    // A member mid-relay is owed space in every bucket its approved action names.
    relayingFiles.forEach(file => {
      const destinations = (file.allocations?.table_ids || []).map(tableId => ({
        tableId, label: file.fileName, key: `routing:${file.id}:${tableId}`,
      }));
      destinations.forEach(({ tableId, label, key }) => {
        const bucket = byTable.get(tableId);
        if (!bucket) return;
        bucket.relaying.push({
          memberId: file.id,
          key,
          fileName: label,
          caption: `Routing to ${bucket.title}...`,
          approvedAt: file.approved_at,
        });
      });
    });

    byTable.forEach(bucket => {
      // Placeholders are owed only where the successor document has not arrived —
      // never one beside the real card for the same member.
      bucket.relaying.forEach(entry => {
        if (bucket.stagedIds.has(entry.memberId)) return;
        bucket.cards.routing.push(entry);
      });
      bucket.approvedStaging.forEach(file => {
        if (bucket.fileIds.has(file.id)) return;
        bucket.cards.preparing.push({
          key: `preparing:${file.id}`,
          fileName: file.fileName,
          caption: 'Preparing file...',
        });
      });
      bucket.allocationEntries = bucket.cards.allocations.map(
        file => ({ tableId: bucket.tableId, file }));
    });

    // Rank, then partition. Buckets already holding work come first whatever the
    // attribute selection rules out — in-flight cards must never sink behind empty
    // buckets — and within each of those groups the destinations that can take the
    // current drop outrank the ones it has ruled out, so the dimmed, unusable empties
    // settle at the bottom together instead of breaking up the live board. filter() is
    // stable, so sidebar order survives inside every rank and a bucket only moves when
    // it gains or loses its last card, or the attribute selection changes what it can
    // accept.
    const all = Array.from(byTable.values());
    const rank = (bucket) => (
      (cardSetCount(bucket.cards) > 0 ? 0 : 2) + (bucket.isCarrier ? 0 : 1));
    return {
      multi,
      tables: [0, 1, 2, 3].flatMap(tier => all.filter(bucket => rank(bucket) === tier)),
    };
  }, [routableTables, hiddenMap, currentAttributeMap, tableViewsById, memberIdsByTable,
    profileSelections, triageFiles, relayingFiles]);

  // Each table bucket's file cards form one section, shown a page at a time, newest
  // upload first (utils/filePages).
  const bucketSections = useMemo(() => Object.fromEntries(board.tables
    .map(bucket => [bucket.tableId, sortNewestFirst(bucket.cards.files)])), [board]);
  // One stable opener per table, so a card's inputs keep their identity between
  // renders and it redraws only when its own file changes.
  const openReviewByTable = useMemo(() => Object.fromEntries(routableTables.map((table) => [
    table.id, (fileId) => setReviewTarget({ tableId: table.id, fileId }),
  ])), [routableTables]);
  const { pages: bucketPages, next: nextBucketPage, previous: previousBucketPage } =
    useFileSectionPages(bucketSections, companyId);

  // Shared validation for both upload paths.
  const partitionChosenFiles = (chosenFiles) => {
    const all = Array.from(chosenFiles);
    const wrongType = all.filter((file) => !isAcceptedUpload(file.name));
    const tooLarge = all.filter((file) => isAcceptedUpload(file.name) && file.size > MAX_UPLOAD_BYTES);
    const valid = all.filter((file) => isAcceptedUpload(file.name) && file.size <= MAX_UPLOAD_BYTES);
    const problems = [];
    if (wrongType.length) {
      problems.push(`Some files were not of an accepted type (${ACCEPTED_UPLOAD_EXTENSIONS.join(', ')}) and were ignored.`);
    }
    if (tooLarge.length) {
      problems.push(`Max file size is ${MAX_UPLOAD_LABEL} — ignored: ${tooLarge.map((f) => f.name).join(', ')}.`);
    }
    return { valid, problems };
  };

  const handleFilesChosen = (chosenFiles) => {
    // One upload owns the selection until it completes: its success path clears
    // `files`, so anything picked meanwhile would be wiped, never uploaded.
    if (isUploading) return;
    const { valid, problems } = partitionChosenFiles(chosenFiles);

    // Selections accumulate — the zone stays a drop target while files are
    // pending, so a second drop adds to the batch. A file that looks like one
    // already selected is held for the user's Keep / Discard decision: a re-pick
    // of the same file would double an ingestion, but a different file with the
    // same metadata is a genuine second upload.
    const { fresh, likely } = splitLikelyDuplicates(files, valid);
    setOverCapBucketId(null);

    if (files.length + fresh.length > MAX_UPLOAD_FILES) {
      setTooManyFiles(true);
      setUploadError(MAX_UPLOAD_FILES_MESSAGE);
      return;
    }

    setTooManyFiles(false);
    setFiles((prev) => [...prev, ...fresh]);
    setUploadError(problems.join(' '));
    if (likely.length > 0) setDuplicatePrompt(likely);
  };

  const handleKeepDuplicates = () => {
    const kept = duplicatePrompt || [];
    setDuplicatePrompt(null);
    if (files.length + kept.length > MAX_UPLOAD_FILES) {
      setTooManyFiles(true);
      setUploadError(MAX_UPLOAD_FILES_MESSAGE);
      return;
    }
    setTooManyFiles(false);
    setFiles((prev) => [...prev, ...kept]);
  };

  const handleFileSelect = (e) => {
    handleFilesChosen(e.target.files);
    // Reset so removing a file then re-picking the same one still fires onChange.
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    handleFilesChosen(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  // Why neither door opens with an attribute its category's type refuses: the
  // value would fail the mandatory checkpoint, or land as null, after the whole
  // pipeline ran (M209). Empty when every pair is sound.
  const invalidAttributeMessage = () => {
    const invalid = invalidPackAttributes(packAttributes, attributeCategories);
    if (invalid.length === 0) return '';
    return 'Upload attributes are not valid for their type: '
      + invalid.map(({ category, label, type }) => `${formatText(category)} value ${label} is not a valid ${type}`).join('; ')
      + '. Remove them or choose another value.';
  };

  const claimUpload = () => {
    if (isSavingPackAttribute) {
      setUploadError('An attribute is being saved. Wait for it to finish, then upload your files.');
      return null;
    }
    if (uploadOperationRef.current) {
      setUploadError('An upload is already in progress — add more files once it finishes.');
      return null;
    }
    const operation = {
      controller: new AbortController(), attributeMap: { ...currentAttributeMap }, started: false,
    };
    uploadOperationRef.current = operation;
    setIsUploading(true);
    return operation;
  };

  const releaseUpload = (operation) => {
    if (uploadOperationRef.current !== operation) return;
    uploadOperationRef.current = null;
    setIsUploading(false);
    setUploadProgress({ current: 0, total: 0 });
  };

  const openExistingPrompt = (prompt) => {
    existingPromptRef.current = prompt;
    setExistingPrompt(prompt);
  };

  const cancelExistingUpload = () => {
    const prompt = existingPromptRef.current;
    if (!prompt) return;
    existingPromptRef.current = null;
    setExistingPrompt(null);
    prompt.operation.controller.abort();
    releaseUpload(prompt.operation);
  };

  const proceedExistingUpload = (skipDuplicates) => {
    const prompt = existingPromptRef.current;
    if (!prompt) return;
    existingPromptRef.current = null;
    setExistingPrompt(null);
    const skip = new Set(skipDuplicates ? prompt.conflicts.map(({ file }) => file) : []);
    return prompt.proceed(prompt.chosen.filter(file => !skip.has(file)));
  };

  const handleBatchUpload = async () => {
    if (uploadOperationRef.current) return;
    setUploadError('');
    setUploadSuccess('');
    if (files.length === 0) {
      setUploadError('Please select files to upload');
      return;
    }
    const attributeProblem = invalidAttributeMessage();
    if (attributeProblem) {
      setUploadError(attributeProblem);
      return;
    }
    const operation = claimUpload();
    if (!operation) return;
    try {
      const existing = await findExistingFilesAcrossTables({ companyId, tables: routableTables, files });
      if (uploadOperationRef.current !== operation) return;
      if (existing.length > 0) {
        openExistingPrompt({
          operation,
          conflicts: existing.map(({ file, tables: holders }) => ({
            file, where: holders.map(formatText).join(', '),
          })),
          chosen: files, scope: 'in a table it could be routed to',
          proceed: chosen => startUpload(chosen, operation),
        });
        return;
      }
      await startUpload(files, operation);
    } catch (error) {
      if (uploadOperationRef.current === operation) {
        console.error('Error checking for existing files:', error);
        setUploadError('Could not check for existing files. Nothing was uploaded.');
        releaseUpload(operation);
      }
    }
  };

  const handleBucketFiles = async (tableId, chosenFiles) => {
    if (uploadOperationRef.current) {
      setUploadError('An upload is already in progress — add more files once it finishes.');
      return;
    }
    setUploadError('');
    setUploadSuccess('');
    const table = tablesById.get(tableId);
    if (!isCarrierNow(table)) {
      setUploadError(`${formatText(table?.name || tableId)}: ${blockedReasonFor(table)}`);
      return;
    }
    const attributeProblem = invalidAttributeMessage();
    if (attributeProblem) {
      setUploadError(attributeProblem);
      return;
    }
    const { valid, problems } = partitionChosenFiles(chosenFiles);
    if (problems.length) setUploadError(problems.join(' '));
    if (valid.length === 0) return;
    if (valid.length > MAX_UPLOAD_FILES) {
      setOverCapBucketId(tableId);
      setUploadError(MAX_UPLOAD_FILES_MESSAGE);
      return;
    }
    setOverCapBucketId(null);
    const operation = claimUpload();
    if (!operation) return;
    try {
      const conflicts = await findExistingFiles({ companyId, tableId, files: valid });
      if (uploadOperationRef.current !== operation) return;
      if (conflicts.length > 0) {
        openExistingPrompt({
          operation, conflicts: conflicts.map(file => ({ file })), chosen: valid,
          scope: `in ${formatText(table?.name || tableId)}`,
          proceed: chosen => startUpload(chosen, operation, tableId),
        });
        return;
      }
      await startUpload(valid, operation, tableId);
    } catch (error) {
      if (uploadOperationRef.current === operation) {
        console.error('Error checking for existing files:', error);
        setUploadError('Could not check for existing files. Nothing was uploaded.');
        releaseUpload(operation);
      }
    }
  };

  const startUpload = async (chosen, operation, tableId = null) => {
    if (uploadOperationRef.current !== operation || operation.started) return;
    if (chosen.length === 0) {
      releaseUpload(operation);
      return;
    }
    operation.started = true;
    setUploadProgress({ current: 0, total: chosen.length });
    try {
      const { failed } = await registerUploadBatch({
        companyId, tableId, workspaceOrigin: Boolean(tableId), files: chosen,
        attributeMap: operation.attributeMap, signal: operation.controller.signal,
        onProgress: (current, total) => {
          if (uploadOperationRef.current === operation) setUploadProgress({ current, total });
        },
      });
      if (uploadOperationRef.current !== operation) return;
      const sent = chosen.length - failed.length;
      if (tableId) {
        setUploadSuccess(sent > 0 ? `Sent ${sent} file${sent === 1 ? '' : 's'} `
          + `to ${formatText(tablesById.get(tableId)?.name || tableId)}` : '');
      } else {
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = null;
        setUploadSuccess(sent > 0 ? `Successfully uploaded ${sent} file(s) for allocation` : '');
      }
      if (failed.length > 0) setUploadError(notRegisteredMessage(failed));
    } catch (error) {
      if (uploadOperationRef.current === operation) {
        console.error('Upload error:', error);
        setUploadError(error.message || 'Failed to upload files');
      }
    } finally {
      releaseUpload(operation);
    }
  };

  const handleTriageSelectionChange = (fileId, tableIds) => {
    triageSelections.current.set(fileId, tableIds);
    setWorkspaceFiles(current => current.map(file =>
      file.id === fileId ? { ...file, selection: tableIds } : file));
  };

  // Start the destination table's data feed immediately, before the relay writes
  // `routing` — the bucket itself already exists.
  const handleTriageApproved = (fileId, action) => {
    triageSelections.current.delete(fileId);
    const uploadId = workspaceFiles.find(f => f.id === fileId)?.upload_id;
    const tableIds = triageActionTableIds(action);
    if (!uploadId || tableIds.length === 0) return;
    setOptimisticTablesByUpload(prev => {
      const next = [...(prev[uploadId] || [])];
      tableIds.forEach(tableId => {
        if (!next.includes(tableId)) next.push(tableId);
      });
      return { ...prev, [uploadId]: next };
    });
  };

  const handleProfileChange = (tableId, fileId, newProfileId) => {
    setProfileSelections(prev => ({ ...prev, [selectionKey(tableId, fileId)]: newProfileId }));
  };

  const clearProfileSelection = (tableId, fileId) => {
    setProfileSelections(prev => {
      const next = { ...prev };
      delete next[selectionKey(tableId, fileId)];
      return next;
    });
  };

  const handleApproveAllocation = async (tableId, fileId, selectedProfile) => {
    const tableView = tableViewsById[tableId];
    const activeProfiles = tableView?.activeProfiles || [];
    // membership check, not a sentinel-string check: the allocator's 'No Match'
    // proposal (or any stale/deleted id) must never reach approve_allocation
    if (!selectedProfile || !activeProfiles.some(p => p.id === selectedProfile)) {
      setUploadError('Please select a data profile before approving.');
      return;
    }
    const profileDoc = activeProfiles.find(p => p.id === selectedProfile);
    const file = (tableView?.stagingFiles || []).find(f => f.id === fileId);
    const missing = missingMandatoryAttributes(
      tablesById.get(tableId)?.attributeColumns || [], allocationAttributes(profileDoc, file));
    if (missing.length > 0) {
      setUploadError(`Cannot approve: data profile "${profileName(profileDoc)}" is missing `
        + `required attribute${missing.length > 1 ? 's' : ''}: ${missing.map(formatText).join(', ')}. `
        + 'Update the profile, then approve again.');
      return;
    }
    try {
      const fileRef = doc(db, `users/${companyId}/tables/${tableId}/unallocated_files`, fileId);
      await updateDoc(fileRef, {
        selected_data_profile: selectedProfile,
        status: 'approved'
      });
      clearProfileSelection(tableId, fileId);
    } catch (err) {
      console.error('Error approving allocation:', err);
      setUploadError('Failed to approve allocation');
    }
  };

  // The allocations the board can approve together. It resolves its own table context
  // per entry and reports what it did rather than writing a message, so "Approve all
  // allocations" produces a single summary.

  // Profile approval. Only files the allocator has settled ('pending') and that carry a
  // REAL profile selection may approve — analysing/splitting cards and 'No Match'
  // proposals stay put.
  const approveAllocations = async (entries) => {
    const isRealProfile = (tableId, id) =>
      !!id && (tableViewsById[tableId]?.activeProfiles || []).some(p => p.id === id);
    // effectiveProfileId: a tie with no selection is nobody's to approve.
    const isCompleteForFile = ({ tableId, file }) => {
      const p = (tableViewsById[tableId]?.activeProfiles || []).find(
        pr => pr.id === effectiveProfileId(file));
      return !!p && missingMandatoryAttributes(
        tablesById.get(tableId)?.attributeColumns || [],
        allocationAttributes(p, file)).length === 0;
    };
    const pendingWithProfile = entries.filter(({ tableId, file }) =>
      file.status === 'pending'
      && isRealProfile(tableId, effectiveProfileId(file)));
    const filesToApprove = pendingWithProfile.filter(isCompleteForFile);

    // Each member on its own: one that cannot be written stays actionable and is
    // named, while the ones written stay approved (M181).
    const { done, failed } = await settleEach(filesToApprove, async ({ tableId, file }) => {
      const fileRef = doc(db, `users/${companyId}/tables/${tableId}/unallocated_files`, file.id);
      await updateDoc(fileRef, {
        selected_data_profile: effectiveProfileId(file),
        status: 'approved'
      });
      clearProfileSelection(tableId, file.id);
    });
    return {
      approved: done.length,
      heldBack: pendingWithProfile.length - filesToApprove.length,
      failed,
    };
  };

  // Hands a member whose relay has outlived its window back to its actionable
  // card, the same bounce the reaper's cap fallback makes. Safe against a relay
  // that is still running: the relay's one transaction refuses a member that is
  // no longer 'approved', so exactly one of the two wins (M220).
  const handleCancelRelay = async (memberId) => {
    try {
      const stagingRef = doc(db, `users/${companyId}/unallocated_files`, memberId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(stagingRef);
        if (!snap.exists() || snap.data().status !== 'approved') return;
        tx.update(stagingRef, {
          status: 'pending',
          error: 'Routing did not finish and was cancelled. Approve again to retry, or delete the file.',
          auto_allocated: deleteField(),
        });
      });
    } catch (error) {
      console.error('Error cancelling routing:', error);
      setUploadError('Failed to cancel routing.');
    }
  };

  const handleDeleteAllocation = async (tableId, fileId) => {
    try {
      const stagingRef = doc(db, `users/${companyId}/tables/${tableId}/unallocated_files`, fileId);
      const midHandover = await runTransaction(db, async (tx) => {
        const stagingSnap = await tx.get(stagingRef);
        if (!stagingSnap.exists()) return false;
        // An approved card is already being handed over to a raw file: the
        // backend has copied its bytes into that file's folder and is about to
        // create the document naming them. Deleting the card now would have the
        // cleanup trigger sweep that copy out from under the raw file (see
        // SmartUploadFiles). The card goes on its own when the handover lands.
        if (stagingSnap.data().status === 'approved') return true;
        const uploadId = stagingSnap.data().upload_id;
        if (uploadId) {
          const uploadRef = doc(db, `users/${companyId}/tables/${tableId}/uploads/${uploadId}`);
          const uploadSnap = await tx.get(uploadRef);
          if (uploadSnap.exists() && uploadSnap.data().raw_manifest) {
            tx.update(uploadRef, { [`raw_manifest.${fileId}`]: deleteField() });
          }
        }
        tx.delete(stagingRef);
        return false;
      });
      // Say so rather than leaving the card sitting there looking ignored.
      if (midHandover) {
        setUploadError('This file is being approved into the table right now and '
          + 'cannot be deleted until that finishes. It will disappear from this '
          + 'list on its own.');
      }
    } catch (error) {
      console.error('Error deleting unallocated file:', error);
      setUploadError('Failed to delete unallocated file.');
    }
  };

  // The confirmation dialog's lines: single-destination files collapse per destination,
  // while a file bound for several is its own line ("2 files → Claims; 1 file copied to
  // Claims & Payments").
  const describeBatchPlan = useCallback((approvable) => {
    const allocateCounts = new Map();
    const fannedOut = [];
    approvable.forEach(({ action }) => {
      const tableIds = triageActionTableIds(action);
      if (tableIds.length === 1) {
        allocateCounts.set(tableIds[0], (allocateCounts.get(tableIds[0]) || 0) + 1);
        return;
      }
      const destinations = tableIds.map(id => formatText(tablesById.get(id)?.name || id));
      fannedOut.push(`1 file copied to ${destinations.join(' & ')}`);
    });
    const allocations = Array.from(allocateCounts.entries()).map(([tableId, count]) =>
      `${count} file${count === 1 ? '' : 's'} → ${formatText(tablesById.get(tableId)?.name || tableId)}`);
    return [...allocations, ...fannedOut];
  }, [tablesById]);

  const prepareBatch = useCallback((row) => ({
    uploadId: row.uploadId,
    batchNo: row.batchNo,
    memberCount: row.memberCount,
    plan: row.plan,
    lines: describeBatchPlan(row.plan.approvable),
  }), [describeBatchPlan]);

  // What Approve ready would touch, read by the button, its hover, the
  // confirmation and the commit alike (M217).
  const readyPlan = useMemo(() => buildReadyPlan(batchRows, prepareBatch), [batchRows, prepareBatch]);
  const destinationsOf = useCallback((action) => triageActionTableIds(action)
    .map(id => formatText(tablesById.get(id)?.name || id)).join(' & '), [tablesById]);
  const readyHover = useMemo(() => readyHoverLines(readyPlan, destinationsOf), [readyPlan, destinationsOf]);

  const commitBatchApprovals = async (batches, { blockedBatches = 0, unreadyBatches = 0 } = {}) => {
    setIsApprovingTriage(true);
    try {
      const results = [];
      const { failed } = await settleEach(batches, async (batch) => {
        const result = await approveWorkspaceFiles(companyId, batch.uploadId, batch.plan.approvable);
        results.push(result);
        result.approved.forEach(({ file, action }) => handleTriageApproved(file.id, action));
      });
      const approved = results.reduce((sum, result) => sum + result.approved.length, 0);
      const skipped = results.flatMap(result => result.skipped);
      const needsAttention = batches.reduce((sum, batch) => sum + batch.plan.skipped, 0);
      setUploadSuccess(`Approved ${approved} file(s).`
        + (needsAttention > 0 ? ` ${needsAttention} file(s) still need attention.` : '')
        + (skipped.length > 0 ? ` Skipped: ${skipped.map(({ file, reason }) => `${file.fileName} (${reason})`).join('; ')}` : '')
        + (blockedBatches > 0 ? ` ${blockedBatches} batch(es) are still uploading or being analysed.` : '')
        + (unreadyBatches > 0 ? ` ${unreadyBatches} batch(es) had no file ready to approve.` : ''));
      if (failed.length > 0) {
        failed.forEach(({ item, error }) => console.error(`Error approving batch ${item.batchNo}:`, error));
        setUploadError(`Could not approve ${describeFailures(failed, (batch) => `Batch ${batch.batchNo}`)}.`);
      }
    } finally {
      setIsApprovingTriage(false);
    }
  };

  // No confirmation: every destination in this batch is already stated on the card and
  // by the bucket the card sits in, so a dialog would only restate the screen.
  const handleApproveBatch = async (row) => {
    const batch = prepareBatch(row);
    if (batch.plan.approvable.length === 0) {
      setUploadError('No files in this batch are ready to allocate — choose a destination table first.');
      return;
    }
    await commitBatchApprovals([batch]);
  };

  // The same per-batch routine over every batch the ready plan names; the
  // batches it leaves alone are reported alongside the result. While nothing is
  // ready the button is disabled, with the reason on hover.
  const handleApproveAllTriage = () => {
    if (readyPlan.readyCount === 0) return;
    // Approve ready is the one action whose reach is not visible in one place — it
    // spans every batch on screen — so it keeps its confirmation.
    setApproveTarget(readyPlan);
  };

  const handleConfirmApprove = async () => {
    if (!approveTarget) return;
    const { batches, blockedBatches, unreadyBatches } = approveTarget;
    setApproveTarget(null);
    await commitBatchApprovals(batches, { blockedBatches, unreadyBatches });
  };

  const renderTriageCard = (file, { withBatchLabel }) => (
    <TriageFileCard
      key={file.id}
      file={file}
      upload={recentUploads.find(upload => upload.id === file.upload_id)}
      companyId={companyId}
      tables={carrierTablesFor(file)}
      selection={file.selection}
      onSelectionChange={handleTriageSelectionChange}
      onApproved={handleTriageApproved}
      onError={setUploadError}
      batchLabel={withBatchLabel ? batchLabelFor(file.upload_id) : null}
    />
  );

  const renderBucketCards = (bucket) => {
    const tableView = bucket.tableId ? tableViewsById[bucket.tableId] : null;
    return (
      <>
        {bucket.cards.triage.map(file => renderTriageCard(file, { withBatchLabel: true }))}
        {bucket.cards.routing.map(placeholder => (
          <TransitionCard
            key={placeholder.key}
            fileName={placeholder.fileName}
            caption={placeholder.caption}
            since={placeholder.approvedAt}
            now={now}
            onCancel={() => handleCancelRelay(placeholder.memberId)}
          />
        ))}
        {bucket.cards.allocations.map(file => (
          <AllocationCard
            key={file.id}
            file={file}
            profiles={tableView?.activeProfiles || []}
            nameById={tableView?.nameById || {}}
            batchLabel={batchLabelFor(file.upload_id)}
            onProfileChange={(fileId, profileId) => handleProfileChange(bucket.tableId, fileId, profileId)}
            onApprove={(fileId, selectedProfile) => handleApproveAllocation(bucket.tableId, fileId, selectedProfile)}
            onDelete={(fileId) => handleDeleteAllocation(bucket.tableId, fileId)}
          />
        ))}
        {bucket.cards.preparing.map(placeholder => (
          <TransitionCard
            key={placeholder.key}
            fileName={placeholder.fileName}
            caption={placeholder.caption}
          />
        ))}
        {(bucketPages[bucket.tableId]?.files || []).map(file => (
          <FileCard
            key={file.id}
            file={file}
            companyId={companyId}
            tableId={bucket.tableId}
            tableName={bucket.title}
            profileNameById={tableView?.nameById || {}}
            onOpen={openReviewByTable[bucket.tableId]}
          />
        ))}
        {bucket.tableId && renderBucketPager(bucket.tableId)}
      </>
    );
  };

  // Previous / Next for a bucket whose file cards span more than one page.
  const renderBucketPager = (tableId) => {
    const page = bucketPages[tableId];
    if (!page || (!page.hasPrevious && !page.hasNext)) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 1 }}>
        <Button size="small" disabled={!page.hasPrevious} onClick={() => previousBucketPage(tableId)}>
          Previous page
        </Button>
        <Typography variant="body2">Page {page.pageNumber}</Typography>
        <Button size="small" disabled={!page.hasNext} onClick={() => nextBucketPage(tableId)}>
          Next page
        </Button>
      </Box>
    );
  };

  const renderBucket = (bucket) => (
    <TableBucket
      key={bucket.key}
      title={bucket.title}
      count={cardSetCount(bucket.cards)}
      isCarrier={bucket.isCarrier}
      blockedReason={bucket.isCarrier ? '' : blockedReasonFor(tablesById.get(bucket.tableId))}
      onFilesDropped={(chosen) => handleBucketFiles(bucket.tableId, chosen)}
      overCap={overCapBucketId === bucket.tableId}
    >
      {renderBucketCards(bucket)}
    </TableBucket>
  );

  const multiCount = cardSetCount(board.multi.cards);
  const visibleBuckets = board.tables.filter(bucket => !bucket.hidden);
  const hiddenBuckets = board.tables.filter(bucket => bucket.hidden);
  // Every allocation on screen waiting on a yes, across every destination — the
  // work set behind one "Approve all allocations". Hidden tables count only while
  // their group is open. Approving a file's tables is one file at a time, in its
  // review dialog: there is no bulk approval of cleaned files (00 A.83).
  const onScreenBuckets = hiddenExpanded ? board.tables : visibleBuckets;
  const boardAllocations = onScreenBuckets.flatMap(bucket => bucket.allocationEntries);
  const hasBoardWork = boardAllocations.length > 0;

  const handleApproveAllOnBoard = async () => {
    setUploadError('');
    setUploadSuccess('');
    setIsApprovingBoard(true);
    try {
      const allocations = await approveAllocations(boardAllocations);
      const heldBackNote = allocations.heldBack
        ? ` ${allocations.heldBack} file(s) held back — their data profiles are missing `
          + 'required attributes.'
        : '';
      const failedNote = allocations.failed.length
        ? ` Could not approve ${allocations.failed.length} file(s): `
          + `${describeFailures(allocations.failed, ({ file }) => file.fileName)}.`
        : '';
      if (!allocations.approved) {
        setUploadError(heldBackNote || failedNote
          ? `No files approved —${heldBackNote}${failedNote}`
          : 'Nothing was ready to approve.');
        return;
      }
      setUploadSuccess(`${allocations.approved} file(s) sent for cleaning.${heldBackNote}`);
      if (failedNote) setUploadError(failedNote.trim());
    } catch (error) {
      console.error('Error approving board work:', error);
      setUploadError('The allocations could not be approved. Try again.');
    } finally {
      setIsApprovingBoard(false);
    }
  };
  // Caps at four across. The ladder is tuned to keep a bucket near the ~320px the cards
  // were designed against once the Data page's rails have taken their width.
  const gridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      lg: 'repeat(3, minmax(0, 1fr))',
      xl: 'repeat(4, minmax(0, 1fr))',
    },
    gap: 2,
    alignItems: 'start',
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Upload Files</Typography>

        {tooManyFiles && (
          <Box sx={{ mb: 1 }}>
            <Chip label={MAX_UPLOAD_FILES_CHIP} color="error" size="small" />
          </Box>
        )}
        <UploadRegistrationErrors companyId={companyId} tables={routableTables} />
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>
            {uploadSuccess}
          </Alert>
        )}

        {isUploading && uploadProgress.total > 0 && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={(uploadProgress.current / uploadProgress.total) * 100}
            />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Uploading {uploadProgress.current} of {uploadProgress.total} files...
            </Typography>
          </Box>
        )}

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'stretch',
          mb: 2,
        }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_UPLOAD_EXTENSIONS.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {/* Upload path — fills everything the attributes panel doesn't take. */}
          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onClick={() => { if (!isUploading) fileInputRef.current.click(); }}
              sx={{
                flexGrow: 1,
                minHeight: 140,
                maxHeight: 260,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                border: '2px dashed',
                borderColor: tooManyFiles ? 'error.main' : (isDraggingOver ? 'primary.main' : 'rgba(255, 255, 255, 0.23)'),
                borderRadius: 1,
                cursor: isUploading ? 'default' : 'pointer',
                p: 2,
                textAlign: 'center',
                backgroundColor: isDraggingOver ? 'action.hover' : 'transparent',
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': isUploading ? {} : {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {files.length > 0 && !isDraggingOver ? (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, mb: 1 }}>
                    {files.map((file, index) => (
                      <Chip
                        key={`${file.name}-${index}`}
                        label={file.name}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        onDelete={isUploading ? undefined : (e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        sx={{ maxWidth: '100%' }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {isUploading ? 'Uploading… new files can be added once it finishes' : '+ click or drag to add more'}
                  </Typography>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    {isUploading
                      ? 'Uploading… new files can be added once it finishes'
                      : isDraggingOver
                      ? 'Drop files here'
                      : 'Click or drag files here to have Decode choose their table'}
                  </Typography>
                </>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleBatchUpload}
              disabled={isUploading || isSavingPackAttribute || files.length === 0}
            >
              {files.length > 0 ? `Upload ${files.length} file${files.length === 1 ? '' : 's'}` : 'Upload'}
            </Button>
            {workspaceAutoTriage && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Auto-allocation is on — files matched to a single table are routed automatically.
              </Typography>
            )}
          </Box>

          {/* Attributes: fixed-width cell pinned to the right edge. */}
          <Box sx={{
            flex: { xs: '1 1 auto', md: '0 0 420px' },
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Attributes
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Attached to every file you upload — and to every row those files produce —
              until you change them.
            </Typography>
            <PackAttributePicker
              companyId={companyId}
              value={packAttributes}
              onChange={setPackAttributes}
              disabled={isUploading}
              isSaving={isSavingPackAttribute}
              onSavingChange={setIsSavingPackAttribute}
            />
          </Box>
        </Box>
      </Paper>

      {tableFeeds.map(({ tableId, memberIds }) => (
        <TableDataFeed
          key={tableId}
          companyId={companyId}
          tableId={tableId}
          tableName={tablesById.get(tableId)?.name}
          memberIds={memberIds}
          onData={handleTableData}
          onError={setUploadError}
        />
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {uploadsLoading ? 'Loading batches…' : `${recentUploads.length} batches loaded`}
          {uploadsChecking > 0 ? ` · checking ${uploadsChecking}` : ''}
          {' · Approval actions apply to loaded work.'}
        </Typography>
        {mayHaveOlderUploads && (
          <Button size="small" disabled={uploadsLoading} onClick={loadOlder}>Show older batches</Button>
        )}
      </Box>
      {uploadsError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={retryUploads}>Retry</Button>}>
          {uploadsError}
        </Alert>
      )}

      {(batchRows.length > 0 || ungroupedTriageFiles.length > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Batches ({batchRows.length})</Typography>
            {batchRows.length > 0 && (
              <Tooltip
                title={readyPlan.readyCount > 0
                  ? readyHover.map((line, index) => (
                    <Typography key={index} variant="caption" display="block">{line}</Typography>
                  ))
                  : noReadyReason(readyPlan)}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleApproveAllTriage}
                    disabled={isApprovingTriage || readyPlan.readyCount === 0}
                  >
                    Approve ready ({readyPlan.readyCount})
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {batchRows.map(row => (
            <BatchRow
              key={row.uploadId}
              row={row}
              isApproving={isApprovingTriage}
              onApproveBatch={handleApproveBatch}
            >
              {row.undecided.length > 0 && (
                <Grid container spacing={2}>
                  {row.undecided.map(file => (
                    <Grid item xs={12} sm={6} md={4} key={file.id}>
                      {renderTriageCard(file, { withBatchLabel: false })}
                    </Grid>
                  ))}
                </Grid>
              )}
            </BatchRow>
          ))}

          {ungroupedTriageFiles.length > 0 && (
            <Box sx={{ pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Unbatched ({ungroupedTriageFiles.length})
              </Typography>
              <Grid container spacing={2}>
                {ungroupedTriageFiles.map(file => (
                  <Grid item xs={12} sm={6} md={4} key={file.id}>
                    {renderTriageCard(file, { withBatchLabel: false })}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6">Tables</Typography>
        <Box sx={{ flexGrow: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
        {hasBoardWork && (
          <Button
            variant="contained"
            onClick={handleApproveAllOnBoard}
            disabled={isApprovingBoard}
          >
            {isApprovingBoard ? 'Approving...' : 'Approve all allocations'}
          </Button>
        )}
      </Box>

      <Box sx={gridSx}>
        {multiCount > 0 && (
          <TableBucket
            title="Multi"
            count={multiCount}
            isMulti
            isDropTarget={false}
          >
            {board.multi.cards.triage.map(file => renderTriageCard(file, { withBatchLabel: true }))}
          </TableBucket>
        )}
        {visibleBuckets.map(renderBucket)}
      </Box>

      {hiddenBuckets.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box
            onClick={() => setHiddenExpanded(!hiddenExpanded)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 1 }}
          >
            {hiddenExpanded ? <ExpandLessIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            <Typography variant="subtitle2" color="text.secondary">
              Hidden ({hiddenBuckets.length})
            </Typography>
          </Box>
          <Collapse in={hiddenExpanded} timeout="auto" unmountOnExit>
            <Box sx={gridSx}>
              {hiddenBuckets.map(renderBucket)}
            </Box>
          </Collapse>
        </Box>
      )}

      {reviewTarget && tablesById.get(reviewTarget.tableId) && (
        <FileReviewDialog
          key={`${reviewTarget.tableId}/${reviewTarget.fileId}`}
          open
          companyId={companyId}
          tableId={reviewTarget.tableId}
          table={tablesById.get(reviewTarget.tableId)}
          fileId={reviewTarget.fileId}
          onClose={() => setReviewTarget(null)}
          onNotice={setUploadError}
        />
      )}

      <ExistingFilesPrompt
        open={Boolean(existingPrompt)}
        conflicts={existingPrompt?.conflicts || []}
        total={existingPrompt?.chosen.length || 0}
        scope={existingPrompt?.scope || ''}
        onCancel={cancelExistingUpload}
        onSkip={() => proceedExistingUpload(true)}
        onUploadAnyway={() => proceedExistingUpload(false)}
      />

      <DuplicateFilesPrompt
        files={duplicatePrompt}
        onKeep={handleKeepDuplicates}
        onDiscard={() => setDuplicatePrompt(null)}
      />

      <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve ready batches</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {(approveTarget?.batches || []).map((batch) => (
              <Box key={batch.uploadId} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Batch {batch.batchNo} ({batch.memberCount} file{batch.memberCount === 1 ? '' : 's'})
                </Typography>
                {batch.lines.map((line, index) => (
                  <Typography key={`${batch.uploadId}-${index}`} variant="body2">{line}</Typography>
                ))}
                {batch.plan.skipped > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {batch.plan.skipped} file{batch.plan.skipped === 1 ? '' : 's'} need attention and
                    will not be approved.
                  </Typography>
                )}
              </Box>
            ))}
            {approveTarget?.blockedBatches > 0 && (
              <Typography variant="body2" color="text.secondary">
                {approveTarget.blockedBatches} batch{approveTarget.blockedBatches === 1 ? '' : 'es'} skipped
                — files still uploading or being analysed.
              </Typography>
            )}
            {approveTarget?.unreadyBatches > 0 && (
              <Typography variant="body2" color="text.secondary">
                {approveTarget.unreadyBatches} batch{approveTarget.unreadyBatches === 1 ? ' has' : 'es have'} no
                file ready to approve and will be left as {approveTarget.unreadyBatches === 1 ? 'it is' : 'they are'}.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)}>Cancel</Button>
          <Button onClick={handleConfirmApprove} variant="contained">Approve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WorkspaceFilesHome;
